import React from "react";
import Link from "next/link";

import { getBrands } from "@/api/requests/getBrands";
import { getUserLocationData } from "@/api/requests/getUserLocation";
import { Brand } from "@/custom-types/Brand";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import { getFirstQueryParam } from "@/utils/nextRouterUtils";
import { getCountryCodeHeader } from "@/utils/requestContext";

import BrandCoins from "@/components/brands/BrandCoins";
import BrandsNearYouEmptyState from "@/components/brands/BrandsNearYou/BrandsNearYouEmptyState";
import Breadcrumb from "@/components/Breadcrumb";
import Breadcrumbs from "@/components/Breadcrumbs";
import PageNavigationV2 from "@/components/PageNavigationV2";

import BrandListHeader from "../../components/brands/BrandListHeader";
import MetaTags from "../../components/MetaTags";

export type BrandIndexPageProps = {
  currentPage: number;
  initialBrands: Array<Brand>;
  searchQuery: string | null;
  totalCount: number;
};

const PER_PAGE = 60;

const BrandIndexPage: React.FC<BrandIndexPageProps> = ({
  currentPage,
  initialBrands,
  searchQuery,
  totalCount,
}) => {
  const pageCount = Math.ceil(totalCount / PER_PAGE);

  return (
    <>
      <MetaTags
        canonicalParams={["page"]}
        description="Explore, find, and learn more about weed farms, weed pen brands, cbd brands, and other makers of the best cannabis products near you."
        title="Shop Weed and CBD Brands Near You"
        robots={searchQuery ? "noindex" : undefined}
      />
      <Breadcrumbs>
        <Breadcrumb href="/brands" currentLocation>
          Brands
        </Breadcrumb>
      </Breadcrumbs>

      <BrandListHeader
        H1Text="Weed and CBD brands near you"
        searchQuery={searchQuery}
      />
      <div className="container">
        {initialBrands.length === 0 && (
          <div className="mt-xl mb-xxl">
            <BrandsNearYouEmptyState
              buttonHref={`/brands/all${searchQuery ? `?search=${searchQuery}` : ""}`}
            />
          </div>
        )}
        {initialBrands.length > 0 && (
          <>
            <BrandCoins initialBrands={initialBrands} />
            <PageNavigationV2
              className="mt-xxl"
              currentPage={currentPage}
              totalPages={pageCount}
            />
            <div className="text-center mt-xxl mb-section">
              <>
                Browse the best and emerging cannabis growers, weed pen and cart
                makers, CBD brands, and more on Leafly. Find{" "}
                <Link href="/dispensaries" className="underline font-bold">
                  dispensaries
                </Link>{" "}
                that carry weed products from your favorite top-shelf weed
                brands while discovering{" "}
                <Link href="/deals" className="underline font-bold">
                  cannabis and CBD deals
                </Link>{" "}
                available near you.
              </>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<BrandIndexPageProps>(
  async (context) => {
    const countryCode = getCountryCodeHeader(context.req.headers);
    const { page, search } = context.query;

    const currentPage = Number(page || 1);

    const searchQuery = getFirstQueryParam(search);

    const toSkip = (currentPage - 1) * PER_PAGE;

    const sortKey = "sort[][recommended]";
    const searchOrSort = searchQuery
      ? { searchTerm: searchQuery }
      : { sort: { [sortKey]: "asc" as const } };

    const { coordinates } = await getUserLocationData(countryCode, context);

    const availableBrandsResponse = await getBrands({
      coordinates: {
        lat: coordinates?.lat || coordinates?.latitude,
        lon: coordinates?.lon || coordinates?.longitude || coordinates?.lng,
      },
      countryCode,
      skip: toSkip,
      take: PER_PAGE,
      ...searchOrSort,
    });

    return {
      props: {
        currentPage: currentPage,
        initialBrands: availableBrandsResponse.data || [],
        searchQuery: searchQuery || null,
        totalCount: availableBrandsResponse.metadata.totalCount,
      },
    };
  },
);

export default BrandIndexPage;
